import { useState, useCallback, useEffect, useRef } from 'react';
import { useToast } from '../contexts/toast-context';
const isSharedSchema = (schema) => !!schema && typeof schema.safeParse === 'function';
/** First message per top-level field */
const sharedSchemaErrors = (schema, data) => {
    const result = schema.safeParse(data);
    const out = {};
    if (result.success)
        return out;
    for (const issue of result.error?.issues ?? []) {
        const key = String(issue.path[0] ?? '_');
        if (!(key in out))
            out[key] = issue.message;
    }
    return out;
};
/**
 * Built-in validators
 */
const validators = {
    required: (value) => {
        if (value === null || value === undefined)
            return false;
        if (typeof value === 'string')
            return value.trim().length > 0;
        if (Array.isArray(value))
            return value.length > 0;
        if (typeof value === 'object') {
            // Handle Date objects specifically
            if (value instanceof Date)
                return !isNaN(value.getTime());
            return Object.keys(value).length > 0;
        }
        return true;
    },
    minLength: (value, length) => {
        return value ? value.trim().length >= length : false;
    },
    maxLength: (value, length) => {
        return value ? value.trim().length <= length : true;
    },
    min: (value, minValue) => {
        return value >= minValue;
    },
    max: (value, maxValue) => {
        return value <= maxValue;
    },
    pattern: (value, pattern) => {
        return pattern.test(value);
    },
    email: (value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(value);
    },
    phone: (value) => {
        // Supports formats: +91 1234567890, 1234567890, +1-234-567-8900
        const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
        return phoneRegex.test(value.replace(/\s/g, ''));
    },
};
/**
 * Get user-friendly field name for error messages
 */
const getFieldLabel = (fieldName) => {
    // Convert camelCase to Title Case
    return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
};
/**
 * Custom hook for form validation
 */
export function useFormValidation(options) {
    const { schema, formData, onValidationError, showToastOnError = true, toastTitle = 'Validation Error' } = options;
    const { showError } = useToast();
    const [errors, setErrors] = useState({});
    const prevFormDataRef = useRef(undefined);
    // Auto-clear field errors when their values change
    useEffect(() => {
        if (!formData || !prevFormDataRef.current) {
            prevFormDataRef.current = formData;
            return;
        }
        const prev = prevFormDataRef.current;
        prevFormDataRef.current = formData;
        setErrors((prevErrors) => {
            if (Object.keys(prevErrors).length === 0)
                return prevErrors;
            let hasChanges = false;
            const newErrors = { ...prevErrors };
            for (const key in formData) {
                if (formData[key] !== prev[key] && newErrors[key]) {
                    delete newErrors[key];
                    hasChanges = true;
                }
            }
            return hasChanges ? newErrors : prevErrors;
        });
    }, [formData]);
    /**
     * Validate a single field
     */
    const validateField = useCallback(async (fieldName, value, formData) => {
        if (isSharedSchema(schema)) {
            return sharedSchemaErrors(schema, { ...(formData ?? {}), [fieldName]: value })[String(fieldName)];
        }
        const fieldValidation = schema[fieldName];
        if (!fieldValidation)
            return undefined;
        const fieldLabel = getFieldLabel(String(fieldName));
        // Required validation
        if (fieldValidation.required) {
            if (!validators.required(value)) {
                const message = typeof fieldValidation.required === 'object'
                    ? fieldValidation.required.message
                    : `${fieldLabel} is required`;
                return message;
            }
        }
        // Skip other validations if value is empty and not required
        if (!validators.required(value)) {
            return undefined;
        }
        // Min length validation
        if (fieldValidation.minLength && typeof value === 'string') {
            if (!validators.minLength(value, fieldValidation.minLength.value)) {
                return fieldValidation.minLength.message;
            }
        }
        // Max length validation
        if (fieldValidation.maxLength && typeof value === 'string') {
            if (!validators.maxLength(value, fieldValidation.maxLength.value)) {
                return fieldValidation.maxLength.message;
            }
        }
        // Min value validation
        if (fieldValidation.min && typeof value === 'number') {
            if (!validators.min(value, fieldValidation.min.value)) {
                return fieldValidation.min.message;
            }
        }
        // Max value validation
        if (fieldValidation.max && typeof value === 'number') {
            if (!validators.max(value, fieldValidation.max.value)) {
                return fieldValidation.max.message;
            }
        }
        // Pattern validation
        if (fieldValidation.pattern && typeof value === 'string') {
            if (!validators.pattern(value, fieldValidation.pattern.value)) {
                return fieldValidation.pattern.message;
            }
        }
        // Email validation
        if (fieldValidation.email && typeof value === 'string') {
            if (!validators.email(value)) {
                const message = typeof fieldValidation.email === 'object'
                    ? fieldValidation.email.message
                    : `Please enter a valid email address`;
                return message;
            }
        }
        // Phone validation
        if (fieldValidation.phone && typeof value === 'string') {
            if (!validators.phone(value)) {
                const message = typeof fieldValidation.phone === 'object'
                    ? fieldValidation.phone.message
                    : `Please enter a valid phone number`;
                return message;
            }
        }
        // Custom validation rules
        if (fieldValidation.custom && Array.isArray(fieldValidation.custom)) {
            for (const rule of fieldValidation.custom) {
                const isValid = await rule.validator(value, formData);
                if (!isValid) {
                    return rule.message;
                }
            }
        }
        return undefined;
    }, [schema]);
    /**
     * Validate entire form
     */
    const validate = useCallback(async (data) => {
        const newErrors = {};
        const errorMessages = [];
        if (isSharedSchema(schema)) {
            for (const [fieldName, error] of Object.entries(sharedSchemaErrors(schema, data))) {
                newErrors[fieldName] = error;
                errorMessages.push(error);
            }
        }
        else {
            // Validate all fields
            for (const fieldName in schema) {
                const value = data[fieldName];
                const error = await validateField(fieldName, value, data);
                if (error) {
                    newErrors[fieldName] = error;
                    errorMessages.push(error);
                }
            }
        }
        setErrors(newErrors);
        const isValid = Object.keys(newErrors).length === 0;
        if (!isValid) {
            // Callback for custom error handling
            if (onValidationError) {
                onValidationError(newErrors);
            }
            // Show toast notification with all errors
            if (showToastOnError) {
                const errorList = errorMessages.slice(0, 5).join('\n• ');
                const additionalErrors = errorMessages.length > 5 ? `\n... and ${errorMessages.length - 5} more` : '';
                showError(toastTitle, `Please fix the following errors:\n• ${errorList}${additionalErrors}`);
            }
        }
        return isValid;
    }, [schema, validateField, onValidationError, showToastOnError, toastTitle, showError]);
    /**
     * Clear all errors
     */
    const clearErrors = useCallback(() => {
        setErrors({});
    }, []);
    /**
     * Clear error for a specific field
     */
    const clearFieldError = useCallback((fieldName) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[fieldName];
            return newErrors;
        });
    }, []);
    /**
     * Set error for a specific field (useful for async validation)
     */
    const setFieldError = useCallback((fieldName, error) => {
        setErrors((prev) => ({
            ...prev,
            [fieldName]: error,
        }));
    }, []);
    return {
        errors,
        isValid: Object.keys(errors).length === 0,
        validate,
        validateField,
        clearErrors,
        clearFieldError,
        setFieldError,
    };
}
